import BaseballResultsProcessor from '../entities/BaseballResultsProcessor.js';
import { filterOnlyHavingParameterSet } from '../filters/byParameterName.js';
import { filterOnlyPitches } from '../filters/byResultTypes.js';
import * as Enums from '../enums/index.js';
import { ResultPair } from '../valueObjects/index.js';
import buildFiltersForPitchingSummary from '../filters/pitchingSummaryFilters.js';
import buildFiltersForHittingSummary from '../filters/hittingSummaryFilters.js';

class FilteredBattingResultsProvider{
    constructor(customResultsProcessor) {
        this.resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    }

    defaultFilters() {
        return {
            // player tagging context
            pitcherIds: [],
            batterIds: [],
            pitcherHandedness: '',
            batterHandedness: '',
            // outcome tagging context
            pitchResults: [],
            pitchTypes: [],
            pitchSet: '',
            zones: [],
            // scoring context
            outs: [],
            balls: [],
            strikes: [],
            runnersOn: [],
            dateFrom: null,
            dateTo: null,
        };
    }

    buildPitchesFiltersArray({
        // tagging context
        pitcherIds, pitcherHandedness, pitchResults, pitchTypes, pitchSet, batterIds, batterHandedness, zones,
        // scoring context
        outs, balls, strikes, runnersOn,
        // session context
        // sessionIds,
        // date range filtering
        dateFrom, dateTo,// season,
    }) {
        let filtersForPitches = buildFiltersForPitchingSummary({
            pitcherIds, pitcherHandedness, pitchResults, pitchTypes, pitchSet, batterIds, batterHandedness, zones,
            outs, balls, strikes, runnersOn,
            dateFrom, dateTo
        });

        // expect BatterTagId property to exist
        filtersForPitches.push({parameter: [Enums.ResultData.BatterTagId.key], operator: 'defined', value: true});

        return filtersForPitches;
    };
    
    buildHitsFiltersArray({
        batterIds, batterHandedness, pitcherHandedness,
        // date time filtering
        dateFrom, dateTo
    }) {
        return buildFiltersForHittingSummary(
            { batterIds, batterHandedness, pitcherHandedness, dateFrom, dateTo }
        );
    };

    extractHits(resultPairs) {
        return resultPairs.reduce((hits, pair) => {
            if (pair.hasHit()) {
                hits.push(pair.getHit());
            }

            return hits;
        }, []);
    }

    extractPitches(resultPairs) {
        return resultPairs.reduce((pitches, pair) => {
            if (pair.hasPitch()) {
                pitches.push(pair.getPitch());
            }

            return pitches;
        }, []);
    }

    filterResults(
        results,
        {
            // tagging context
            pitcherIds, pitcherHandedness, pitchResults, pitchTypes, pitchSet, batterIds, batterHandedness, zones,
            // scoring context
            outs, balls, strikes, runnersOn,
            // session context
            // sessionIds,
            // date range filtering
            dateFrom, dateTo,// season,
        } = {}
    ) {
        const pitchingFilters = this.buildPitchesFiltersArray({
            pitcherIds, pitcherHandedness, pitchResults, pitchTypes, pitchSet, batterIds, batterHandedness, zones,
            outs, balls, strikes, runnersOn,
            dateFrom, dateTo
        });
        const hittingFilters = this.buildHitsFiltersArray({ batterIds, batterHandedness, pitcherHandedness, dateFrom, dateTo });
        const filteredPitches = this.resultsProcessor.filterData(results, pitchingFilters);
        const allPitches = this.resultsProcessor.filterData(results, filterOnlyPitches());
        const preFilteredHits = this.resultsProcessor.filterData(results, hittingFilters);

        // now we need to get only hits without matching pitch (using allPitches) and hits with matching pitch from filteredPitches
        const filteredHitsMatchingToAllPitches = this.resultsProcessor.matchPitchesAndHits(preFilteredHits.concat(allPitches));
        const filteredHitsWithoutMatchingPitch = this.resultsProcessor.filterData(filteredHitsMatchingToAllPitches, [
            { parameter: [ResultPair.getPitchProperty()], operator: 'empty', value: null },
            { parameter: [ResultPair.getHitProperty()], operator: 'notEmpty', value: null },
        ]);

        const filteredHitsMatchingToFilteredPitches = this.resultsProcessor.matchPitchesAndHits(preFilteredHits.concat(filteredPitches));
        const filteredHitsWithMatchingFilteredPitch = this.resultsProcessor.filterData(filteredHitsMatchingToFilteredPitches, [
            { parameter: [ResultPair.getPitchProperty()], operator: 'notEmpty', value: null },
            { parameter: [ResultPair.getHitProperty()], operator: 'notEmpty', value: null },
        ]);

        const filteredHits = this.extractHits(filteredHitsWithoutMatchingPitch.concat(filteredHitsWithMatchingFilteredPitch));
        const filteredPairs = this.resultsProcessor.matchPitchesAndHits(filteredPitches.concat(filteredHits));

        return filteredPairs;
    }
}

export default FilteredBattingResultsProvider;